import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import FormSelect from "../../Generic/FormSelect";
import { Button, CircularProgress } from "@mui/material";
import { getThemeColor } from "../../../utils/applyTheme";

const FilterPanel = ({ filters, onFilterChange, onTermRegistrationChange, universityId, onShowStudents, loading, register, errors, setValue }) => {
  const { t, i18n } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);

  const [termRegistrations, setTermRegistrations] = useState([]);
  const [selectedTermRegistration, setSelectedTermRegistration] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);

  useEffect(() => {
    console.log('[LANG DEBUG FilterPanel] Academic years useEffect fired. i18n.language:', i18n.language);
    const loadAcademicYears = async () => {
      try {
        const response = await axiosPrivate.get(`/StaffPortalExamRegistrations/GetAcademicYearsWithOpenGrades/${universityId}`);
        console.log('[LANG DEBUG FilterPanel] Academic years API response:', JSON.stringify(response.data));
        setAcademicYears(response.data);
        if (selectedAcademicYear) {
          const updated = response.data.find(a => a.value === selectedAcademicYear.value);
          console.log('[LANG DEBUG FilterPanel] Updating selected academic year:', selectedAcademicYear.label, '->', updated?.label);
          if (updated) setSelectedAcademicYear(updated);
        }
      } catch (error) {
        console.error("Error loading academic years:", error);
      }
    };

    if (universityId) {
      loadAcademicYears();
    }
  }, [universityId, i18n.language]);

  useEffect(() => {
    console.log('[LANG DEBUG FilterPanel] Term registrations useEffect fired. i18n.language:', i18n.language);
    const loadTermRegistrations = async () => {
      if (!filters.academicYearId) {
        setTermRegistrations([]);
        return;
      }

      try {
        const response = await axiosPrivate.get(
          `/StaffPortalExamRegistrations/MyTermRegistrationsWithOpenGrades?academicYearId=${filters.academicYearId}`
        );
        console.log('[LANG DEBUG FilterPanel] Term registrations API response:', JSON.stringify(response.data));
        setTermRegistrations(response.data);
        if (selectedTermRegistration) {
          const updated = response.data.find(tr => tr.value === selectedTermRegistration.value);
          console.log('[LANG DEBUG FilterPanel] Updating selected term:', selectedTermRegistration.label, '->', updated?.label);
          if (updated) setSelectedTermRegistration(updated);
        }
      } catch (error) {
        console.error("Error loading term registrations:", error);
      }
    };

    loadTermRegistrations();
  }, [filters.academicYearId, i18n.language]);

  useEffect(() => {
    console.log('[LANG DEBUG FilterPanel] Subjects useEffect fired. i18n.language:', i18n.language);
    const loadSubjects = async () => {
      if (!filters.termRegistrationId) {
        setSubjects([]);
        return;
      }

      try {
        const response = await axiosPrivate.get(
          `/StaffPortalExamRegistrations/MySubjectsWithOpenGrades?termRegistrationId=${filters.termRegistrationId}`
        );
        console.log('[LANG DEBUG FilterPanel] Subjects API response:', JSON.stringify(response.data));
        setSubjects(response.data);
        if (selectedSubject) {
          const updated = response.data.find(s => s.value === selectedSubject.value);
          console.log('[LANG DEBUG FilterPanel] Updating selected subject:', selectedSubject.label, '->', updated?.label);
          if (updated) setSelectedSubject(updated);
        }
      } catch (error) {
        console.error("Error loading subjects:", error);
      }
    };

    loadSubjects();
  }, [filters.termRegistrationId, i18n.language]);

  const handleSelectChange = (field, selectedOption) => {
    setValue(field, selectedOption?.value, { shouldValidate: true });

    switch (field) {
      case 'academicYearId':
        setSelectedAcademicYear(selectedOption);
        onFilterChange("academicYearId", selectedOption ? selectedOption.value : null);
        setSelectedTermRegistration(null);
        setSelectedSubject(null);
        onFilterChange("termRegistrationId", null);
        onFilterChange("subjectId", null);
        setValue('termRegistrationId', null);
        setValue('subjectId', null);
        if (onTermRegistrationChange) {
          onTermRegistrationChange(null);
        }
        break;
      case 'termRegistrationId':
        setSelectedTermRegistration(selectedOption);
        onFilterChange("termRegistrationId", selectedOption ? selectedOption.value : null);
        setSelectedSubject(null);
        onFilterChange("subjectId", null);
        setValue('subjectId', null);
        if (onTermRegistrationChange) {
          onTermRegistrationChange(selectedOption);
        }
        break;
      case 'subjectId':
        setSelectedSubject(selectedOption);
        onFilterChange("subjectId", selectedOption ? selectedOption.value : null);
        break;
      default:
        break;
    }
  };

  const [displayDate, setDisplayDate] = React.useState('');

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}.${month}.${year}`;
  };

  React.useEffect(() => {
    if (filters.examDate) {
      setDisplayDate(formatDateForDisplay(filters.examDate));
    } else {
      setDisplayDate('');
    }
  }, [filters.examDate]);

  const handleExamDateChange = (event) => {
    let value = event.target.value;

    if (value === '' || value.length === 0) {
      setDisplayDate('');
      onFilterChange("examDate", '');
      return;
    }

    value = value.replace(/[^\d.]/g, '');

    const digitsOnly = value.replace(/\./g, '');
    let formatted = '';

    if (digitsOnly.length > 0) {
      formatted = digitsOnly.slice(0, 2);
      if (digitsOnly.length >= 3) {
        formatted += '.' + digitsOnly.slice(2, 4);
      }
      if (digitsOnly.length >= 5) {
        formatted += '.' + digitsOnly.slice(4, 8);
      }
    }

    setDisplayDate(formatted);

    if (formatted.length === 10) {
      const [day, month, year] = formatted.split('.');
      if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
        const date = `${year}-${month}-${day}`;
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          onFilterChange("examDate", date);
          return;
        }
      }
    }

    onFilterChange("examDate", '');
  };

  const handleNativeDateChange = (event) => {
    const value = event.target.value;
    if (value) {
      onFilterChange("examDate", value);
      setDisplayDate(formatDateForDisplay(value));
    } else {
      onFilterChange("examDate", '');
      setDisplayDate('');
    }
  };

  return (
    <>
      <div className="row justify-content-start">
        <FormSelect
          label={t("academicYear")}
          name="academicYearId"
          register={register}
          errors={errors}
          options={academicYears}
          colSize={2}
          isClearable={false}
          required={false}
          value={selectedAcademicYear}
          onChange={(selectedOption) => handleSelectChange('academicYearId', selectedOption)}
        />
        <FormSelect
          label={t("termRegistration")}
          name="termRegistrationId"
          register={register}
          errors={errors}
          options={termRegistrations}
          colSize={2}
          isClearable={false}
          required={false}
          disabled={!filters.academicYearId}
          value={selectedTermRegistration}
          onChange={(selectedOption) => handleSelectChange('termRegistrationId', selectedOption)}
        />
        <FormSelect
          label={t("subject")}
          name="subjectId"
          register={register}
          errors={errors}
          options={subjects}
          colSize={3}
          isClearable={false}
          required={false}
          disabled={!filters.termRegistrationId}
          value={selectedSubject}
          onChange={(selectedOption) => handleSelectChange('subjectId', selectedOption)}
        />
        <div className="col-lg-2 mb-3">
          <div className="form-group">
            <label className="labelDescription">
              <span className="labelText">{t("examDate")}</span>
              <span className="labelStar text-danger fw-bold">*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className={`form-control ${!filters.examDate && filters.subjectId ? 'is-invalid' : ''}`}
                value={displayDate}
                onChange={handleExamDateChange}
                disabled={!filters.subjectId}
                placeholder="dd.MM.yyyy"
                maxLength="10"
                style={{ paddingRight: '35px' }}
              />
              <input
                type="date"
                onChange={handleNativeDateChange}
                disabled={!filters.subjectId}
                value={filters.examDate || ''}
                style={{
                  position: 'absolute',
                  right: '0',
                  top: '0',
                  width: '35px',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: filters.subjectId ? '#6c757d' : '#adb5bd',
                }}
              >
                📅
              </span>
            </div>
            {!filters.examDate && filters.subjectId && (
              <div className="invalid-feedback d-block">
                {t("required")}
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-2 mb-3">
          <div className="form-group">
            <label className="labelDescription">
              <span className="labelText" style={{ visibility: 'hidden' }}>.</span>
            </label>
            <Button
              variant="contained"
              onClick={onShowStudents}
              disabled={!filters.termRegistrationId || !filters.subjectId || loading}
              fullWidth
              sx={{
                height: '38px',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                backgroundColor: getThemeColor('primary', '#dc3545'),
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                boxShadow: `0 2px 5px ${getThemeColor('primary', '#dc3545')}40`,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: getThemeColor('hover', '#c82333'),
                  boxShadow: `0 4px 10px ${getThemeColor('primary', '#dc3545')}59`,
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  backgroundColor: getThemeColor('hover', '#c82333'),
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 4px ${getThemeColor('primary', '#dc3545')}40`,
                },
                '&:disabled': {
                  backgroundColor: getThemeColor('primary', '#dc3545'),
                  color: '#fff',
                  opacity: 0.6,
                  transform: 'none',
                },
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t("show")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
